import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
    forwardRef,
} from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    useGlobalFilter,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import * as naturalCompare from 'string-natural-compare';

import {
    createFuzzyTextFilter,
    formatTableNumber,
    formatTableString,
    getScaledColumnWidths,
    getScrollbarWidth,
    locale,
} from '@trqk/utils';
import {
    DollarsCellLong,
    GlobalFilter,
    PlaysCell,
    ProgramCell,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TimeCodeCell,
    TruncatedTextCell,
    TruncatedTwoRowCell,
} from '@trqk/components';

import { columnIds, columnWidthInputs } from '../constants';

const rowHeight = 50;
const defaultColumnWidth = 100;

interface PerformancesTableProps {
    /**
     * JSX to render as empty state, or null if none is appropriate
     */
    emptyState: null | JSX.Element;
    /**
     * Rendered JSX for filters, which filter the `items` array before it is passed to
     * PerformancesTable
     */
    filters: ReactNode;
    /**
     * Height of table
     */
    height: number;
    /**
     * Array of PerformanceTableItem elements
     */
    items: PerformanceTableItem[];
    /**
     * A timestamp for refreshing the table. When this changes, it indicates the table should be
     * updated with new data
     */
    refreshTime?: number;
    /**
     * Width of table
     */
    width: number;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * A table of statement performance data, featuring filters and column sorting.
 * @param props - see {@link PerformancesTableProps}
 * @returns JSX.Element
 */
export const PerformancesTable = forwardRef<
    HTMLDivElement,
    PerformancesTableProps
>(
    (
        {
            emptyState,
            filters,
            height,
            items,
            refreshTime = 0,
            width,
        }: PerformancesTableProps,
        ref,
    ): JSX.Element => {
        const [data, setData] = useState(items);

        // Only reset the items data state when refreshTime is greater than zero
        useEffect(() => {
            if (refreshTime > 0) {
                setData(items);
            }
        }, [items, refreshTime]);

        const defaultColumn = useMemo(
            () => ({
                width: defaultColumnWidth,
            }),
            [],
        );

        const columns = useMemo(() => {
            const scaledColumnWidths = getScaledColumnWidths(
                width,
                columnWidthInputs,
            );

            return [
                {
                    id: columnIds.program,
                    Header: TableHeader,
                    headerLabel: 'Program / Episode',
                    accessor: ({
                        Episode,
                        Show,
                    }: PerformanceTableItem): {
                        Episode: string;
                        Show: string;
                    } => ({
                        Episode,
                        Show,
                    }),
                    sortType: (
                        rowA: Row<PerformanceTableItem>,
                        rowB: Row<PerformanceTableItem>,
                    ): number => {
                        const showA = rowA?.original?.Show;
                        const showB = rowB?.original?.Show;
                        const episodeA = rowA?.original?.Episode;
                        const episodeB = rowB?.original?.Episode;
                        const sourceA = rowA?.original?.Source;
                        const sourceB = rowB?.original.Source;
                        const titleA = rowA?.original?.['Work Title'];
                        const titleB = rowB?.original?.['Work Title'];

                        if (showA.length > 0 && showB.length > 0) {
                            return naturalCompare(
                                `${showA}${episodeA}`,
                                `${showB}${episodeB}`,
                            );
                        }

                        if (sourceA.length > 0 && sourceB.length > 0) {
                            return naturalCompare(sourceA, sourceB);
                        }

                        return naturalCompare(titleA, titleB);
                    },
                    Cell: ProgramCell,
                    width:
                        scaledColumnWidths?.[columnIds.program] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.workTitle,
                    Header: TableHeader,
                    headerLabel: 'Work Title',
                    accessor: (performance: PerformanceTableItem): string =>
                        formatTableString(performance['Work Title']),
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.workTitle] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.workId,
                    Header: TableHeader,
                    headerLabel: 'PRO ID',
                    accessor: (performance: PerformanceTableItem): string =>
                        formatTableString(performance['Work ID']),
                    Cell: TruncatedTextCell,
                    width:
                        scaledColumnWidths?.[columnIds.workId] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.paymentType,
                    Header: TableHeader,
                    headerLabel: 'Payment Type',
                    accessor: ({ paymentType }: PerformanceTableItem): string =>
                        formatTableString(paymentType),
                    Cell: TruncatedTextCell,
                    width:
                        scaledColumnWidths?.[columnIds.paymentType] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.publisher,
                    Header: TableHeader,
                    headerLabel: 'Publisher',
                    accessor: ({ publisher }: PerformanceTableItem): string =>
                        formatTableString(publisher ?? ''),
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.publisher] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.writer,
                    Header: TableHeader,
                    headerLabel: 'Writer',
                    accessor: ({ writer }: PerformanceTableItem): string =>
                        formatTableString(writer ?? ''),
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.writer] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.dayPart,
                    Header: TableHeader,
                    headerLabel: 'Day Part',
                    accessor: (performance: PerformanceTableItem): string =>
                        formatTableString(performance['Day Part']),
                    Cell: TruncatedTextCell,
                    width:
                        scaledColumnWidths?.[columnIds.dayPart] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.musicUserGenre,
                    Header: TableHeader,
                    headerLabel: 'Media',
                    accessor: (performance: PerformanceTableItem): string =>
                        formatTableString(performance['Music User Genre']),
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.musicUserGenre] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.source,
                    Header: TableHeader,
                    headerLabel: 'Channel',
                    accessor: ({ Source }: PerformanceTableItem): string =>
                        formatTableString(Source),
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.source] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.duration,
                    Header: TableHeader,
                    headerLabel: 'Duration',
                    accessor: ({ Duration }: PerformanceTableItem): number =>
                        formatTableNumber(Duration),
                    Cell: TimeCodeCell,
                    width:
                        scaledColumnWidths?.[columnIds.duration] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.countryCode,
                    Header: TableHeader,
                    headerLabel: 'Territory',
                    accessor: (performance: PerformanceTableItem): string =>
                        formatTableString(performance['Country Code']),
                    Cell: TruncatedTextCell,
                    width:
                        scaledColumnWidths?.[columnIds.countryCode] ??
                        defaultColumnWidth,
                    tooltipPosition: 'left',
                },
                {
                    id: columnIds.plays,
                    Header: TableHeader,
                    headerLabel: 'Plays',
                    accessor: ({ Plays }: PerformanceTableItem): number =>
                        formatTableNumber(Plays),
                    Cell: PlaysCell,
                    width:
                        scaledColumnWidths?.[columnIds.plays] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.earnings,
                    Header: TableHeader,
                    headerLabel: 'Earnings',
                    accessor: ({ Earnings }: PerformanceTableItem): number =>
                        formatTableNumber(Earnings),
                    Cell: DollarsCellLong,
                    width:
                        scaledColumnWidths?.[columnIds.earnings] ??
                        defaultColumnWidth,
                },
            ];
        }, [width]);

        const filterTypes = useMemo(
            () => ({
                globalFilter: createFuzzyTextFilter<PerformanceTableItem>({
                    keys: [
                        'values.countryCode',
                        'values.dayPart',
                        'values.duration',
                        'values.earnings',
                        'values.musicUserGenre',
                        'values.paymentType',
                        'values.plays',
                        'values.program.Episode',
                        'values.program.Show',
                        'values.publisher',
                        'values.source',
                        'values.workId',
                        'values.workTitle',
                        'values.writer',
                    ],
                }),
            }),
            [],
        );

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            totalColumnsWidth,
            prepareRow,
            state,
            setGlobalFilter,
        } = useTable<PerformanceTableItem>(
            {
                columns,
                data,
                defaultColumn,
                filterTypes,
                globalFilter: filterTypes.globalFilter,
                autoResetGlobalFilter: false,
            },
            useBlockLayout,
            useGlobalFilter,
            useSortBy,
        );

        /* END table management logic */

        const RenderRow = useCallback(
            ({ index, style }) => {
                const row = rows[index];

                prepareRow(row);

                return (
                    <TableRow<PerformanceTableItem>
                        index={index}
                        style={style}
                        row={row}
                    />
                );
            },
            [prepareRow, rows],
        );

        const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

        return (
            <>
                <div className="flex justify-between pt-6 px-6 pb-4">
                    <h4>{locale.enUS.STATEMENTS.PERFORMANCES.TITLE}</h4>
                    <div className="w-1/3">
                        <GlobalFilter
                            globalFilter={state.globalFilter}
                            setGlobalFilter={setGlobalFilter}
                        />
                    </div>
                </div>
                <div
                    {...getTableProps()}
                    className="trqk-table text-sm font-normal mx-6"
                >
                    {filters}
                    <div className="mt-4">
                        {headerGroups.map(
                            (
                                headerGroup: HeaderGroup<PerformanceTableItem>,
                            ): JSX.Element => {
                                const { key: headerGroupKey } =
                                    headerGroup.getHeaderGroupProps();

                                return (
                                    <TableHeaderGroup<PerformanceTableItem>
                                        key={headerGroupKey}
                                        headerGroup={headerGroup}
                                    />
                                );
                            },
                        )}
                    </div>

                    <div ref={ref} {...getTableBodyProps()}>
                        {!emptyState && (
                            <FixedSizeList
                                height={height}
                                itemCount={rows.length}
                                itemSize={rowHeight}
                                width={totalColumnsWidth + scrollbarWidth}
                            >
                                {RenderRow}
                            </FixedSizeList>
                        )}
                        {emptyState && emptyState}
                    </div>
                </div>
            </>
        );
    },
);
